import * as React from 'react';
import { useState, createContext, useContext, ReactNode, useEffect } from 'react';
import * as moment from "moment";
import { validate } from 'uuid';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';

export const DefaultPaging: Paging = {
    page: 1,
    size: 10,
}

const DefaultRefresh = async () => {
    return {
        data: [],
        '@odata.count': 0,
    }
}

export type Condition = 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le' | 'all' | 'any';

export type Direction = 'asc' | 'desc';

export interface Filter {
    field: string;
    condition: Condition;
    primarySubClauseField?: string;
    primarySubQueryCondition?: Condition;
    subQueryConditions?: SubQueryCondition[];
    values: any[];
}


export interface SubQueryCondition {
    subClauseField: string;
    subCondition: Condition;
    value: any;
}

export interface Order {
    field: string;
    condition: Direction;
}

interface Paging {
    page: number;
    size: number;
}

export interface ODataParams {
    filter: string;
    orderby: string;
    top: number;
    skip: number;
}

interface QueryState {
    filters: Filter[];
    ordering: Order[];
    paging: Paging;
    count: number;
    data: any[];
    applyFilters: (filters: Filter[]) => void;
    applyOrdering: (orders: Order[]) => void;
    applyPaging: (paging: Paging) => void;
    refresh: () => void;
    refreshing: boolean;
}

const QueryContext = createContext<QueryState>({
    filters: [],
    ordering: [],
    paging: DefaultPaging,
    count: 0,
    data: [],
    applyFilters: () => { },
    applyOrdering: () => { },
    applyPaging: () => { },
    refresh: () => { },
    refreshing: false,
});

const QueryProvider = QueryContext.Provider;

export const useQuery = () => useContext(QueryContext);

interface QueryProps {
    initialFilters?: Filter[];
    onFiltersApplied?: (filters: Filter[]) => void;
    initialOrdering?: Order[];
    initialPaging?: Paging;
    onRefresh?: (params: ODataParams) => Promise<any>;
    children?: ReactNode;
}

export const Query = ({ initialFilters = [], onFiltersApplied = null, initialOrdering = [], initialPaging = DefaultPaging, onRefresh = DefaultRefresh, children}: QueryProps) => {
    const [filters, setFilters] = useState(initialFilters);
    const [ordering, setOrdering] = useState(initialOrdering);
    const [paging, setPaging] = useState(initialPaging);
    const [count, setCount] = useState(0);
    const [data, setData] = useState<any[]>([]);
    const [refreshing, setRefreshing] = useState(true);

    useEffect(() => {
        handleRefresh();
    }, [filters, ordering, paging]);

    const handleRefresh = async () => {

        setRefreshing(true);

        const { page, size } = paging;
        const skip = size * (page - 1);

        const filter = filters.map(f => `(${f.values.map(x => {
            const formatValue = (value: any) => validate(value) || (typeof x === "boolean") || (typeof x === "number") || moment(value, "YYYY-MM-DD", true).isValid() || value === null ? value : `'${value}'`;

            const value = formatValue(x);

            let filter = `${f.field} ${f.condition} ${value}`;

            if (f.condition === 'all' || f.condition === 'any') {

                const generateSubQueryItems = (): string => {
                    if (f.subQueryConditions !== null && f.subQueryConditions !== undefined) {
                        let subQueryItems: string = '';

                        for (let subQuery of f.subQueryConditions) {
                            let subQueryValue = formatValue(subQuery.value);

                            subQueryItems += ` and c/${subQuery.subClauseField} ${subQuery.subCondition} ${subQueryValue}`;
                        }

                        return subQueryItems;
                    }
                    return '';
                }

                filter = `${f.field}/${f.condition}(c: c/${f.primarySubClauseField} ${f.primarySubQueryCondition} ${value} ${generateSubQueryItems()})`;
            }

            return filter
        })
            .join(' or ')})`)
            .join(' and ');

        const order = ordering.map(o => `${o.field} ${o.condition}`).join(', ');
        const params: ODataParams = {
            filter: filter.length === 0
                ? undefined
                : filter,
            orderby: order.length === 0
                ? undefined
                : order,
            top: size,
            skip: skip,
        }

        const result = await onRefresh(params);
        setCount(result['@odata.count']);
        setData(result.value);
        setRefreshing(false);
    }

    const handleFiltersApplying = (filters: Filter[]) => {
        if (onFiltersApplied != null) {
            onFiltersApplied(filters);
        }

        setFilters(filters)
    }

    const state: QueryState = {
        filters: filters,
        ordering: ordering,
        paging: paging,
        count: count,
        data: data,
        applyFilters: handleFiltersApplying,
        applyOrdering: setOrdering,
        applyPaging: setPaging,
        refresh: handleRefresh,
        refreshing: refreshing,
    }

    return (
        <QueryProvider value={state}>
            {children}
        </QueryProvider>
    );
}

export const getAnalysisSetsQcBlindingFilter = (field = "type") : Filter => {
    return {
        field: `indexof(${field}, 'QC')`,
        condition: 'eq',
        values: [-1],
    }
}
